import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Check, Upload, PenLine, CreditCard, ShieldCheck, Sparkles, Home, KeyRound, Wifi, MapPin, Loader2, Clock, FileText } from "lucide-react";
import { GuestDarkToggle, useGuestDarkMode } from "@/hooks/useGuestDarkMode";

const BASE = process.env.REACT_APP_BACKEND_URL;
const API = `${BASE}/api`;

const money = (n) => `£${(Number(n) || 0).toFixed(2)}`;

function StepPill({ index, label, done, current }) {
  return (
    <div className={`flex items-center gap-2 ${current ? "text-slate-900" : done ? "text-teal-700" : "text-slate-400"}`}>
      <div className={`w-6 h-6 rounded-full border flex items-center justify-center text-[11px] font-medium ${
        done ? "bg-teal-600 text-white border-teal-600" : current ? "bg-white border-slate-900" : "bg-white border-slate-200"
      }`}>
        {done ? <Check className="w-3.5 h-3.5" /> : index}
      </div>
      <span className="text-xs uppercase tracking-wider">{label}</span>
    </div>
  );
}

export default function GuestJourney() {
  // Session Z81/Z82 — signed-cookie dark-mode preference sticks across visits.
  useGuestDarkMode();
  const { token } = useParams();
  const [search] = useSearchParams();
  const nav = useNavigate();
  const [journey, setJourney] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [prefs, setPrefs] = useState({ arrival_time: "", dietary: "", has_dog: false, guests_count: "", special_requests: "" });
  const [signName, setSignName] = useState("");
  const [selectedUpsells, setSelectedUpsells] = useState([]);

  const paidFlag = search.get("paid");
  const canceledFlag = search.get("canceled");

  const load = async () => {
    setLoading(true);
    try {
      const url = paidFlag ? `/public/journeys/${token}/refresh` : `/public/journeys/${token}`;
      const { data } = await axios.get(`${API}${url}`);
      setJourney(data);
      setPrefs({
        arrival_time: data.preferences?.arrival_time || "",
        dietary: data.preferences?.dietary || "",
        has_dog: !!data.preferences?.has_dog,
        guests_count: data.preferences?.guests_count || "",
        special_requests: data.preferences?.special_requests || "",
      });
      setSignName(data.signed_name || (data.guest?.name || ""));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Check-in link not found");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    if (paidFlag) {
      toast.success(`${paidFlag === "balance" ? "Balance paid" : paidFlag === "deposit" ? "Deposit authorised" : "Add-ons confirmed"}. Thank you!`);
      nav(`/journey/${token}`, { replace: true });
    }
    if (canceledFlag) {
      toast.error("Payment cancelled");
      nav(`/journey/${token}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const progress = journey?.progress || {};
  const cfg = journey?.config || {};
  const brand = journey?.brand || { brand_name: "Newport Stays", accent_color: "#0d7a75" };
  const accent = brand.accent_color || "#0d7a75";

  const steps = useMemo(() => {
    if (!journey) return [];
    return [
      { key: "preferences", label: "Preferences", done: !!progress.preferences_saved_at },
      { key: "id", label: "ID", done: !!progress.id_uploaded_at, skip: !cfg.require_id },
      { key: "esign", label: "House rules", done: !!progress.esign_signed_at, skip: !cfg.require_esign },
      { key: "balance", label: "Balance", done: !!progress.payment_completed_at, skip: !cfg.require_balance },
      { key: "deposit", label: "Deposit", done: !!progress.deposit_authorized_at, skip: !cfg.require_deposit },
    ].filter((s) => !s.skip);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journey]);

  const currentStepKey = steps.find((s) => !s.done)?.key || "portal";
  const allComplete = steps.every((s) => s.done);

  function renderGuidebookSection(key) {
    if (!journey) return null;
    const p = journey.property || {};
    switch (key) {
      case "door_code":
        return p.door_code ? (
          <div data-guidebook-section="door_code">
            <InfoTile icon={<KeyRound />} label="Door code" value={p.door_code} />
          </div>
        ) : null;
      case "wifi":
        return p.wifi_ssid ? (
          <div data-guidebook-section="wifi">
            <InfoTile icon={<Wifi />} label={`Wi-Fi · ${p.wifi_ssid}`} value={p.wifi_password} />
          </div>
        ) : null;
      case "checkin_instructions":
        return p.checkin_instructions ? (
          <div data-guidebook-section="checkin_instructions">
            <Section icon={<Home className="w-4 h-4" />} title="How to check in" accent={accent}>
              <div className="whitespace-pre-wrap text-sm text-slate-700">{p.checkin_instructions}</div>
            </Section>
          </div>
        ) : null;
      case "guestbook_recommendations":
        return p.guestbook_recommendations ? (
          <div data-guidebook-section="guestbook_recommendations">
            <Section icon={<MapPin className="w-4 h-4" />} title="Local recommendations" accent={accent}>
              <div className="whitespace-pre-wrap text-sm text-slate-700">{p.guestbook_recommendations}</div>
            </Section>
          </div>
        ) : null;
      case "guestbook_house_rules":
        return p.guestbook_house_rules ? (
          <div data-guidebook-section="guestbook_house_rules">
            <Section icon={<ShieldCheck className="w-4 h-4" />} title="House rules" accent={accent}>
              <div className="whitespace-pre-wrap text-sm text-slate-700">{p.guestbook_house_rules}</div>
            </Section>
          </div>
        ) : null;
      case "guestbook_checkout":
        return p.guestbook_checkout ? (
          <div data-guidebook-section="guestbook_checkout">
            <Section icon={<Clock className="w-4 h-4" />} title="Check-out instructions" accent={accent}>
              <div className="whitespace-pre-wrap text-sm text-slate-700">{p.guestbook_checkout}</div>
            </Section>
          </div>
        ) : null;
      case "guestbook_emergency_contact":
        return p.guestbook_emergency_contact ? (
          <div data-guidebook-section="guestbook_emergency_contact">
            <Section icon={<ShieldCheck className="w-4 h-4" />} title="In case of emergency" accent={accent}>
              <div className="text-sm text-slate-700">{p.guestbook_emergency_contact}</div>
            </Section>
          </div>
        ) : null;
      case "upsells_available":
        return (journey.upsells || []).length > 0 ? (
          <div data-guidebook-section="upsells_available">
            <Section icon={<Sparkles className="w-4 h-4" />} title="Still available" accent={accent}>
              <div className="grid md:grid-cols-2 gap-3">
                {journey.upsells.map((u) => {
                  const already = (journey.purchased_upsells || []).some((pp) => pp.id === u.id);
                  const selected = selectedUpsells.includes(u.id);
                  return (
                    <label key={u.id} className={`rounded-lg border p-4 flex items-start gap-3 cursor-pointer ${
                      already ? "bg-teal-50 border-teal-200 opacity-70 cursor-default" : selected ? "border-teal-500 bg-teal-50/40" : "border-slate-200 hover:border-slate-300"
                    }`}>
                      <input type="checkbox" disabled={already} checked={already || selected}
                        onChange={(e) => setSelectedUpsells((s) => e.target.checked ? [...s, u.id] : s.filter((x) => x !== u.id))}
                        className="mt-1" />
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <div className="font-medium text-sm">{u.name}</div>
                          <div className="text-sm">{money(u.price)}</div>
                        </div>
                        {u.description && <div className="text-xs text-slate-500 mt-1">{u.description}</div>}
                        {already && <div className="text-[11px] text-teal-700 mt-1 uppercase tracking-wider">Purchased</div>}
                      </div>
                    </label>
                  );
                })}
              </div>
              {selectedUpsells.length > 0 && (
                <div className="mt-5">
                  <Button onClick={() => startCheckout("upsells")} disabled={busy}
                    style={{ backgroundColor: accent }}
                    className="rounded-full h-11 px-6 text-white text-sm">
                    Add {selectedUpsells.length} item{selectedUpsells.length === 1 ? "" : "s"}
                  </Button>
                </div>
              )}
            </Section>
          </div>
        ) : null;
      default:
        return null;
    }
  }

  // Guidebook analytics — fire one view event per section per session as it scrolls into view
  const trackedRef = useRef(new Set());
  useEffect(() => {
    if (!allComplete || !journey) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const key = entry.target.getAttribute("data-guidebook-section");
        if (!key || trackedRef.current.has(key)) return;
        trackedRef.current.add(key);
        axios.post(`${API}/public/journeys/${token}/guidebook-view`, { section: key }).catch(() => {});
      });
    }, { threshold: 0.4 });
    document.querySelectorAll("[data-guidebook-section]").forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [allComplete, journey, token]);

  const savePrefs = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/public/journeys/${token}/preferences`, {
        ...prefs,
        guests_count: prefs.guests_count ? Number(prefs.guests_count) : null,
      });
      toast.success("Preferences saved");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const uploadId = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/public/journeys/${token}/id`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("ID uploaded");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const sign = async () => {
    if (!signName.trim()) { toast.error("Type your full name to sign"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/public/journeys/${token}/sign`, { signed_name: signName, agreed: true });
      toast.success("House rules signed");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign failed");
    } finally {
      setBusy(false);
    }
  };

  const startCheckout = async (purpose) => {
    setBusy(true);
    try {
      const origin_url = window.location.origin;
      let path = "";
      let body = { origin_url };
      if (purpose === "balance") path = "/pay";
      else if (purpose === "deposit") path = "/deposit";
      else if (purpose === "upsells") { path = "/upsells"; body.upsell_ids = selectedUpsells; }
      const { data } = await axios.post(`${API}/public/journeys/${token}${path}`, body);
      if (data.already_paid || data.already_authorized) {
        toast.success("Already done — refreshing…"); await load(); return;
      }
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Payment error");
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 text-slate-500 text-sm">
        <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading your check-in…
      </div>
    );
  }
  if (!journey) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 text-slate-500 text-sm">
        This link is no longer active.
      </div>
    );
  }

  const heroBg = journey.property?.hero_image_url || "https://images.unsplash.com/photo-1499793983690-e29da59ef1c2?auto=format&fit=crop&w=1600&q=80";

  return (
    <div className="min-h-screen bg-stone-50">
      <header
        style={{ backgroundImage: `linear-gradient(rgba(10,17,40,0.72), rgba(10,17,40,0.72)), url(${heroBg})` }}
        className="text-white bg-cover bg-center px-6 md:px-14 py-14 relative"
      >
        <div className="absolute top-4 right-4 z-10">
          <GuestDarkToggle className="!bg-white/10 !border-white/20 !text-white hover:!bg-white/20 hover:!border-white/40 hover:!text-white" />
        </div>
        <div className="max-w-4xl mx-auto">
          <div className="text-[11px] tracking-[0.22em] uppercase" style={{ color: `${accent}` }}>
            {brand.brand_name} · Guest check-in
          </div>
          <h1 className="font-serif text-4xl md:text-5xl mt-3 leading-tight">
            {allComplete ? `Welcome, ${journey.guest?.name?.split(" ")[0] || "guest"}` : `Get ready for ${journey.property?.name || "your stay"}`}
          </h1>
          <div className="mt-3 text-white/70 text-sm flex flex-wrap gap-x-6 gap-y-1">
            <span className="inline-flex items-center gap-2"><Clock className="w-4 h-4" /> {journey.booking?.check_in} → {journey.booking?.check_out}</span>
            {journey.booking?.guests_count && <span>{journey.booking.guests_count} guests</span>}
            {journey.property?.address && <span className="inline-flex items-center gap-2"><MapPin className="w-4 h-4" /> {journey.property.address}</span>}
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 md:px-12 py-10">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3 mb-10">
          {steps.map((s, i) => (
            <StepPill key={s.key} index={i + 1} label={s.label} done={s.done} current={currentStepKey === s.key} />
          ))}
          <StepPill index={steps.length + 1} label="Guidebook" done={allComplete} current={currentStepKey === "portal"} />
        </div>

        {journey.care_tips && (
          <section className="rounded-2xl border border-slate-200 p-6 mb-8" style={{ borderLeft: `4px solid ${accent}` }} data-testid="journey-care-tips">
            <div className="font-serif text-xl inline-flex items-center gap-2"><Home className="w-4 h-4" style={{ color: accent }} /> {journey.care_tips.title}</div>
            <p className="text-sm text-slate-500 mt-2">{journey.care_tips.intro}</p>
            <ul className="mt-3 space-y-1.5 text-sm">
              {journey.care_tips.tips.map((t, i) => (
                <li key={i} className="flex gap-2" data-testid={`journey-care-tip-${i}`}><Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: accent }} /> {t}</li>
              ))}
            </ul>
          </section>
        )}

        {!allComplete && (
          <div className="space-y-6">
            <Section
              icon={<Sparkles className="w-4 h-4" />}
              title="Your preferences"
              done={!!progress.preferences_saved_at}
              accent={accent}
              current={currentStepKey === "preferences"}
            >
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="ns-label">Arrival time</Label>
                  <Input
                    data-testid="journey-arrival-time"
                    className="mt-2 h-11 bg-white"
                    placeholder="Around 4pm"
                    value={prefs.arrival_time}
                    onChange={(e) => setPrefs({ ...prefs, arrival_time: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="ns-label">Total guests</Label>
                  <Input
                    type="number" min={1}
                    className="mt-2 h-11 bg-white"
                    value={prefs.guests_count}
                    onChange={(e) => setPrefs({ ...prefs, guests_count: e.target.value })}
                  />
                </div>
                <div className="md:col-span-2">
                  <Label className="ns-label">Dietary needs / allergies</Label>
                  <Input
                    className="mt-2 h-11 bg-white"
                    placeholder="Gluten-free, nut allergy…"
                    value={prefs.dietary}
                    onChange={(e) => setPrefs({ ...prefs, dietary: e.target.value })}
                  />
                </div>
                <div className="md:col-span-2">
                  <Label className="ns-label">Special requests</Label>
                  <Textarea
                    rows={3}
                    className="mt-2 bg-white"
                    placeholder="Anything else we should know?"
                    value={prefs.special_requests}
                    onChange={(e) => setPrefs({ ...prefs, special_requests: e.target.value })}
                  />
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={prefs.has_dog} onChange={(e) => setPrefs({ ...prefs, has_dog: e.target.checked })} />
                  Bringing a well-behaved dog
                </label>
              </div>
              <div className="mt-5">
                <Button
                  data-testid="journey-save-preferences"
                  onClick={savePrefs}
                  disabled={busy}
                  style={{ backgroundColor: accent }}
                  className="rounded-full h-11 px-6 text-white text-sm"
                >
                  {busy ? "Saving…" : progress.preferences_saved_at ? "Update preferences" : "Save preferences"}
                </Button>
              </div>
            </Section>

            {cfg.require_id && (
              <Section
                icon={<Upload className="w-4 h-4" />}
                title="Upload photo ID"
                done={!!progress.id_uploaded_at}
                accent={accent}
                current={currentStepKey === "id"}
              >
                <p className="text-sm text-slate-500 mb-4">
                  Snap a photo of your passport or driving licence. We only use this for check-in
                  verification and store it securely.
                </p>
                {progress.id_uploaded_at ? (
                  <div className="text-sm text-teal-700 inline-flex items-center gap-2">
                    <Check className="w-4 h-4" /> ID received {new Date(progress.id_uploaded_at).toLocaleString("en-GB")}
                    <label className="ml-4 text-xs text-slate-400 underline cursor-pointer">
                      Replace
                      <input type="file" data-testid="journey-id-replace" accept="image/*,application/pdf" className="hidden" onChange={uploadId} />
                    </label>
                  </div>
                ) : (
                  <label className="inline-flex items-center gap-2 rounded-full h-11 px-6 text-white text-sm cursor-pointer" style={{ backgroundColor: accent }}>
                    <Upload className="w-4 h-4" /> {busy ? "Uploading…" : "Choose file"}
                    <input type="file" data-testid="journey-id-upload" accept="image/*,application/pdf" className="hidden" onChange={uploadId} disabled={busy} />
                  </label>
                )}
              </Section>
            )}

            {cfg.require_esign && (
              <Section
                icon={<PenLine className="w-4 h-4" />}
                title="Sign the house rules"
                done={!!progress.esign_signed_at}
                accent={accent}
                current={currentStepKey === "esign"}
              >
                {/* Session Z49 — if the property owner uploaded a bespoke
                    rental-agreement PDF, link it right above the signing
                    box so the guest can download the full version. */}
                {journey.property?.documents?.agreement && (
                  <a
                    data-testid="journey-agreement-download"
                    href={`${API}/public/journeys/${token}/documents/agreement`}
                    target="_blank"
                    rel="noreferrer"
                    className="mb-3 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white hover:border-teal-600 hover:text-teal-700 text-xs text-slate-700 px-3 py-1.5"
                    title={journey.property.documents.agreement.original_filename}
                  >
                    <FileText className="w-3.5 h-3.5" />
                    Download the full rental agreement (PDF)
                  </a>
                )}
                <div className="text-sm text-slate-700 rounded-lg bg-stone-50 border border-slate-100 p-4 max-h-40 overflow-auto whitespace-pre-wrap">
                  {journey.property?.guestbook_house_rules ||
                    "By signing you agree to respect quiet hours between 10pm and 8am, treat the property with care, and not exceed the maximum guest count. Full house rules will be available in your guidebook after check-in."}
                </div>
                <div className="mt-4">
                  <Label className="ns-label">Type your full name to sign</Label>
                  <Input
                    data-testid="journey-sign-name"
                    className="mt-2 h-11 bg-white"
                    value={signName}
                    onChange={(e) => setSignName(e.target.value)}
                    disabled={!!progress.esign_signed_at}
                  />
                </div>
                {progress.esign_signed_at ? (
                  <div className="mt-3 text-sm text-teal-700 inline-flex items-center gap-2">
                    <Check className="w-4 h-4" /> Signed by {journey.signed_name} on {new Date(progress.esign_signed_at).toLocaleString("en-GB")}
                  </div>
                ) : (
                  <Button
                    data-testid="journey-sign-btn"
                    onClick={sign}
                    disabled={busy}
                    style={{ backgroundColor: accent }}
                    className="mt-4 rounded-full h-11 px-6 text-white text-sm"
                  >
                    {busy ? "Signing…" : "Agree & sign"}
                  </Button>
                )}
              </Section>
            )}

            {cfg.require_balance && (
              <Section
                icon={<CreditCard className="w-4 h-4" />}
                title="Pay the balance"
                done={!!progress.payment_completed_at}
                accent={accent}
                current={currentStepKey === "balance"}
                right={<span className="text-slate-500 text-sm">{money(cfg.balance_amount)}</span>}
              >
                {progress.payment_completed_at ? (
                  <div className="text-sm text-teal-700 inline-flex items-center gap-2">
                    <Check className="w-4 h-4" /> Balance paid on {new Date(progress.payment_completed_at).toLocaleString("en-GB")}
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-slate-500 mb-4">
                      Secure payment via Stripe. Your card is charged immediately for the stay balance.
                    </p>
                    <Button
                      data-testid="journey-pay-balance"
                      onClick={() => startCheckout("balance")}
                      disabled={busy}
                      style={{ backgroundColor: accent }}
                      className="rounded-full h-11 px-6 text-white text-sm"
                    >
                      Pay {money(cfg.balance_amount)}
                    </Button>
                  </>
                )}
              </Section>
            )}

            {cfg.require_deposit && (
              <Section
                icon={<ShieldCheck className="w-4 h-4" />}
                title="Authorise damage deposit"
                done={!!progress.deposit_authorized_at}
                accent={accent}
                current={currentStepKey === "deposit"}
                right={<span className="text-slate-500 text-sm">{money(cfg.deposit_amount)} hold</span>}
              >
                {progress.deposit_authorized_at ? (
                  <div className="text-sm text-teal-700 inline-flex items-center gap-2">
                    <Check className="w-4 h-4" /> Deposit authorised — released automatically after check-out
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-slate-500 mb-4">
                      We&apos;ll place a temporary hold of <strong>{money(cfg.deposit_amount)}</strong> on your card.
                      Nothing is charged unless there&apos;s damage — the hold is released after your stay.
                    </p>
                    <Button
                      data-testid="journey-authorize-deposit"
                      onClick={() => startCheckout("deposit")}
                      disabled={busy}
                      style={{ backgroundColor: accent }}
                      className="rounded-full h-11 px-6 text-white text-sm"
                    >
                      Authorise {money(cfg.deposit_amount)}
                    </Button>
                  </>
                )}
              </Section>
            )}

            {journey.upsells?.length > 0 && (
              <Section
                icon={<Sparkles className="w-4 h-4" />}
                title="Optional extras"
                done={false}
                accent={accent}
                current={false}
              >
                <div className="grid md:grid-cols-2 gap-3">
                  {journey.upsells.map((u) => {
                    const already = (journey.purchased_upsells || []).some((p) => p.id === u.id);
                    const selected = selectedUpsells.includes(u.id);
                    return (
                      <label key={u.id} data-testid={`journey-upsell-${u.id}`} className={`rounded-lg border p-4 flex items-start gap-3 cursor-pointer transition-colors ${
                        already ? "bg-teal-50 border-teal-200 opacity-70 cursor-default" : selected ? "border-teal-500 bg-teal-50/40" : "border-slate-200 hover:border-slate-300"
                      }`}>
                        <input
                          type="checkbox"
                          disabled={already}
                          checked={already || selected}
                          onChange={(e) => setSelectedUpsells((s) => e.target.checked ? [...s, u.id] : s.filter((x) => x !== u.id))}
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <div className="font-medium text-sm text-slate-900">{u.name}</div>
                            <div className="text-sm text-slate-700">{money(u.price)}</div>
                          </div>
                          {u.description && <div className="text-xs text-slate-500 mt-1">{u.description}</div>}
                          {already && <div className="text-[11px] text-teal-700 mt-1 uppercase tracking-wider">Purchased</div>}
                        </div>
                      </label>
                    );
                  })}
                </div>
                {selectedUpsells.length > 0 && (
                  <div className="mt-5">
                    <Button
                      data-testid="journey-buy-upsells"
                      onClick={() => startCheckout("upsells")}
                      disabled={busy}
                      style={{ backgroundColor: accent }}
                      className="rounded-full h-11 px-6 text-white text-sm"
                    >
                      Buy {selectedUpsells.length} item{selectedUpsells.length === 1 ? "" : "s"}
                    </Button>
                  </div>
                )}
              </Section>
            )}
          </div>
        )}

        {allComplete && (
          <div className="space-y-6">
            <div className="ns-card p-8 border-l-4" style={{ borderColor: accent }}>
              <div className="ns-label" style={{ color: accent }}>You&apos;re all set</div>
              <div className="font-serif text-3xl mt-2 text-slate-900">Your check-in is complete</div>
              <p className="text-sm text-slate-500 mt-2">
                Below is everything you need for your stay. Save this page — you can come back
                to it any time.
              </p>
            </div>

            {/* Session Z49 — bespoke property-guide PDF (if the owner uploaded one). */}
            {journey.property?.documents?.guide && (
              <a
                data-testid="journey-guide-download"
                href={`${API}/public/journeys/${token}/documents/guide`}
                target="_blank"
                rel="noreferrer"
                className="ns-card p-6 flex items-center gap-4 hover:border-teal-600 transition-colors"
                title={journey.property.documents.guide.original_filename}
              >
                <div className="w-11 h-11 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: `${accent}18`, color: accent }}>
                  <FileText className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="ns-label" style={{ color: accent }}>Property guide</div>
                  <div className="font-serif text-lg text-slate-900 mt-0.5 truncate">
                    {journey.property.documents.guide.original_filename}
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5">
                    {Math.round((journey.property.documents.guide.size || 0) / 1024)} KB · updated {new Date(journey.property.documents.guide.uploaded_at).toLocaleDateString("en-GB")}
                  </div>
                </div>
                <div className="text-xs text-teal-700 uppercase tracking-wider shrink-0">Download</div>
              </a>
            )}

            <div className="grid md:grid-cols-2 gap-4">
              {renderGuidebookSection("door_code")}
              {renderGuidebookSection("wifi")}
            </div>

            {(journey.guidebook_order || [
              "checkin_instructions","guestbook_recommendations","guestbook_house_rules",
              "guestbook_checkout","guestbook_emergency_contact","upsells_available",
            ]).filter((k) => k !== "door_code" && k !== "wifi").map((key) => (
              <div key={key}>{renderGuidebookSection(key)}</div>
            ))}
          </div>
        )}

        <div className="mt-12 text-center text-xs text-slate-400 tracking-wider">
          © {new Date().getFullYear()} {brand.brand_name}
          {brand.powered_by_visible && brand.brand_name !== "Newport Stays" && (
            <div className="mt-1 normal-case">Powered by Newport Stays</div>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ icon, title, done, current, accent, right, children }) {
  return (
    <div className={`ns-card p-6 md:p-8 ${current ? "ring-2" : ""}`} style={current ? { boxShadow: `0 0 0 2px ${accent}22` } : {}}>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ backgroundColor: `${accent}12`, color: accent }}>
          {icon}
        </div>
        <div className="flex-1">
          <div className="ns-label" style={{ color: accent }}>{done ? "Complete" : current ? "Next up" : "Section"}</div>
          <div className="font-serif text-xl text-slate-900">{title}</div>
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}

function InfoTile({ icon, label, value }) {
  return (
    <div className="ns-card p-6">
      <div className="ns-label inline-flex items-center gap-2">{icon}{label}</div>
      <div className="font-serif text-2xl mt-2 tracking-wider">{value || "—"}</div>
    </div>
  );
}
